import React, { useState, useEffect } from 'react';
import { FiTrash2, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TrendingList = ({ onToggleActive }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/list-featured`);
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data = await response.json();
      setProducts(data.featured_products || []);
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to load products: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/delete-featured/${productId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete product');
      }

      // Update local state to remove the deleted product
      setProducts(products.filter(product => product.id !== productId));
      toast.success('Product deleted successfully!');
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`);
    }
  };

  const handleToggleActive = async (productId) => {
    try {
      // First update the UI optimistically
      setProducts(products.map(product => 
        product.id === productId 
          ? { ...product, isActive: !product.isActive } 
          : product
      ));

      // Then make the API call
      await onToggleActive(productId);
      toast.success('Product status updated!');
    } catch (err) {
      // Revert if API call fails
      setProducts(products.map(product => 
        product.id === productId 
          ? { ...product, isActive: !product.isActive } 
          : product
      ));
      toast.error(`Failed to update status: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading products...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">Error: {error}</div>;
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((product) => (
          <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            {/* Product Image */}
            <div className="relative h-48 overflow-hidden">
              <img
                src={product.image}
                alt="Product"
                className="w-full h-full object-cover"
              />
              {/* Status Badge */}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => handleToggleActive(product.id)}
                  className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                >
                  {product.isActive ? (
                    <FiToggleRight className="text-indigo-600 text-xl" />
                  ) : (
                    <FiToggleLeft className="text-gray-400 text-xl" />
                  )}
                </button>
              </div>
            </div>

            {/* Product Info */}
            <div className="p-4">
              <div className="flex justify-between items-start">
                {/* Type Badge */}
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    product.type === 'trending'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {product.type ? product.type.charAt(0).toUpperCase() + product.type.slice(1) : 'Featured'}
                </span>

                {/* Delete Button */}
                <button
                  onClick={() => handleDeleteProduct(product.id)}
                  className="p-1 text-red-500 hover:text-red-700 transition-colors"
                >
                  <FiTrash2 className="text-lg" />
                </button>
              </div>

              {/* Product Details */}
              <div className="mt-2">
                <h3 className="font-medium text-gray-900">{product.name || 'Product Name'}</h3>
                <p className="text-sm text-gray-500">{product.id || 'ID: N/A'}</p>
                {product.expirationDate && (
                  <p className="text-xs text-gray-400 mt-1">
                    Expires: {new Date(product.expirationDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default TrendingList;